import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { formatNumber } from "shared/utils/numberFormatter";
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableDataCellClass,
} from "features/activity/styles/dataTableStyles";
import { makeFormatValue } from "features/analytics/studio/utils/axisFormat";
import BreakdownTopValuesControl from "./BreakdownTopValuesControl";

const tableStaticRowClass = "h-8 border-b border-[color:var(--border-subtle)] last:border-b-0";

/** Stable DOM id for breakdown rows (sorting must not swap React state keys). */
function breakdownRowId(segmentLabel) {
  const s = String(segmentLabel ?? "").trim() || "(none)";
  return `bd-${encodeURIComponent(s).replace(/%/g, "_")}`;
}

function getNumericSortValue(row, column) {
  if (column === "total") {
    const n = row.totalProfilesSum;
    return Number.isFinite(n) ? n : null;
  }
  const idx = Number(column);
  if (!Number.isFinite(idx) || idx < 0) return null;
  const v = row.values?.[idx];
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function compareBreakdownRows(a, b, sortMode, valueColumn) {
  if (sortMode === "segment-asc") {
    return String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" });
  }
  if (sortMode === "segment-desc") {
    return String(b.label || "").localeCompare(String(a.label || ""), undefined, { sensitivity: "base" });
  }
  const ascending = sortMode === "value-asc";
  const va = getNumericSortValue(a, valueColumn);
  const vb = getNumericSortValue(b, valueColumn);
  const aMissing = va == null;
  const bMissing = vb == null;
  if (aMissing && bMissing) {
    return String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" });
  }
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (va === vb) {
    return String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" });
  }
  return ascending ? va - vb : vb - va;
}

/**
 * RetentionCohortTable - Shows cohort matrix (rows = cohorts, columns = intervals)
 * Mirrors FunnelDataTable structure
 */
export default function RetentionCohortTable({
  intervals = [],
  cohorts = [],
  weightedAverage = null,
  breakdowns = [],
  /** Human-readable dimension name from query (e.g. propertyLabel); shown as first column header when breakdown rows exist */
  breakdownDimensionLabel = "",
  metric = "retention_rate",
  config = {},
  /** Chart-only top-N; table always shows full breakdown list */
  breakdownChartTopN,
  onBreakdownChartTopNChange,
}) {
  const leftY = config?.axis?.leftY ?? {};
  const yAxisConfig = leftY;
  const formatYValue = makeFormatValue(yAxisConfig);

  const hasBreakdowns = Array.isArray(breakdowns) && breakdowns.length > 0;
  const firstColumnHeader =
    hasBreakdowns && String(breakdownDimensionLabel || "").trim()
      ? String(breakdownDimensionLabel).trim()
      : "Date";
  const [expandedSegments, setExpandedSegments] = useState(() => ({}));
  /** segment-asc | segment-desc | value-asc | value-desc */
  const [breakdownSortMode, setBreakdownSortMode] = useState("segment-asc");
  /** 'total' or interval column index (number) when using value-* sort */
  const [valueSortColumn, setValueSortColumn] = useState("total");
  const [breakdownSortMenuOpen, setBreakdownSortMenuOpen] = useState(false);
  const [valueSubmenuHover, setValueSubmenuHover] = useState(null);
  const breakdownSortTriggerRef = useRef(null);
  const breakdownSortMenuRef = useRef(null);
  const [breakdownMenuPos, setBreakdownMenuPos] = useState({ top: 0, left: 0 });

  const weightedAverageRow =
    weightedAverage && Array.isArray(weightedAverage.values) && weightedAverage.values.length > 0
      ? {
          label: "Weighted Average",
          cohortSize: "100%",
          values: intervals.map((_, idx) => weightedAverage.values[idx] ?? 0),
          isIncomplete: intervals.map((_, idx) =>
            Array.isArray(weightedAverage.isComplete) ? weightedAverage.isComplete[idx] === false : false
          ),
          isSummary: true,
        }
      : null;

  const tableRows = weightedAverageRow ? [weightedAverageRow, ...cohorts] : cohorts;
  const breakdownRows = useMemo(() => {
    if (!hasBreakdowns) return [];
    return breakdowns.map((bd) => {
      const segmentLabel =
        bd?.segment != null && String(bd.segment).trim() !== "" ? String(bd.segment) : "(none)";
      const values = Array.isArray(bd?.values)
        ? intervals.map((_, idx) => bd.values[idx] ?? 0)
        : new Array(intervals.length).fill(0);
      const isIncomplete = Array.isArray(bd?.isComplete)
        ? intervals.map((_, idx) => bd.isComplete[idx] !== true)
        : new Array(intervals.length).fill(false);
      const childRows = (Array.isArray(bd?.cohorts) ? bd.cohorts : []).map((c) => {
        const cohortSize = Number(c?.cohortSize) || 0;
        const retained = Array.isArray(c?.retained) ? c.retained : [];
        const childIncomplete = Array.isArray(c?.isComplete)
          ? intervals.map((_, idx) => c.isComplete[idx] !== true)
          : new Array(intervals.length).fill(false);
        const childValues = intervals.map((_, idx) => {
          const retainedUsers = Number(retained[idx]) || 0;
          if (metric === "retention_rate") {
            return cohortSize > 0 ? (retainedUsers / cohortSize) * 100 : 0;
          }
          return retainedUsers;
        });
        return {
          label: c?.label || "Unknown",
          cohortSize,
          values: childValues,
          isIncomplete: childIncomplete,
          isChild: true,
        };
      });
      const totalProfilesSum = childRows.reduce((acc, c) => acc + (Number(c.cohortSize) || 0), 0);
      return {
        id: breakdownRowId(segmentLabel),
        label: segmentLabel,
        cohortSize: "100%",
        totalProfilesSum,
        values,
        isIncomplete,
        children: childRows,
        isParent: true,
      };
    });
  }, [breakdowns, hasBreakdowns, intervals, metric]);

  const sortedBreakdownRows = useMemo(() => {
    if (!hasBreakdowns || breakdownRows.length === 0) return [];
    const copy = [...breakdownRows];
    copy.sort((a, b) => compareBreakdownRows(a, b, breakdownSortMode, valueSortColumn));
    return copy;
  }, [breakdownRows, breakdownSortMode, valueSortColumn, hasBreakdowns]);

  const toggleSegment = (segmentId) => {
    setExpandedSegments((prev) => ({ ...prev, [segmentId]: !prev[segmentId] }));
  };

  useEffect(() => {
    if (!breakdownSortMenuOpen) return undefined;
    const updatePos = () => {
      const el = breakdownSortTriggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setBreakdownMenuPos({ top: r.bottom + 4, left: r.left });
    };
    updatePos();
    const onDoc = (e) => {
      const t = e.target;
      if (breakdownSortMenuRef.current?.contains(t)) return;
      if (breakdownSortTriggerRef.current?.contains(t)) return;
      setBreakdownSortMenuOpen(false);
      setValueSubmenuHover(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setBreakdownSortMenuOpen(false);
        setValueSubmenuHover(null);
      }
    };
    const onScroll = () => updatePos();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [breakdownSortMenuOpen]);

  const valueColumnOptions = useMemo(() => {
    const opts = [{ key: "total", label: "# Total Profile(s)" }];
    intervals.forEach((label, idx) => {
      opts.push({ key: idx, label: `# ${label}` });
    });
    return opts;
  }, [intervals]);

  const applyValueSort = (direction, columnKey) => {
    setBreakdownSortMode(direction === "asc" ? "value-asc" : "value-desc");
    setValueSortColumn(columnKey);
    setBreakdownSortMenuOpen(false);
    setValueSubmenuHover(null);
  };

  if (intervals.length === 0 || (!hasBreakdowns && cohorts.length === 0)) {
    return (
      <div className="w-full p-4 text-center text-sm" style={{ color: theme.textMuted }}>
        No data available
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className={tableHeaderRowClass}>
            <th
              className={tableHeaderCellClass}
              style={{ borderRight: `1px solid ${theme.border}` }}
            >
              {hasBreakdowns ? (
                <div className="flex flex-col items-start gap-1" style={{ textTransform: "none" }}>
                  <div className="inline-flex items-center gap-1" ref={breakdownSortTriggerRef}>
                  <span>
                    {firstColumnHeader}
                    {breakdownRows.length > 0 ? ` ${breakdownRows.length}` : ""}
                  </span>
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={breakdownSortMenuOpen}
                    onClick={() => {
                      setBreakdownSortMenuOpen((o) => !o);
                      if (breakdownSortMenuOpen) setValueSubmenuHover(null);
                    }}
                    className="inline-flex items-center justify-center rounded p-0.5"
                    style={{
                      color: theme.textMuted,
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                    }}
                    title="Sort breakdown"
                  >
                    <svg width="10" height="10" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  {breakdownSortMenuOpen &&
                    createPortal(
                      <div
                        ref={breakdownSortMenuRef}
                        role="menu"
                        style={{
                          position: "fixed",
                          top: breakdownMenuPos.top,
                          left: breakdownMenuPos.left,
                          display: "flex",
                          alignItems: "stretch",
                          backgroundColor: theme.bgRaised || "var(--surface-base)",
                          border: `1px solid ${theme.border}`,
                          borderRadius: 10,
                          boxShadow: "0 10px 28px rgba(15, 23, 42, 0.16)",
                          zIndex: 9999,
                          fontFamily: "Inter, sans-serif",
                          textTransform: "none",
                        }}
                        onMouseLeave={() => setValueSubmenuHover(null)}
                      >
                        <div style={{ minWidth: 200, padding: "8px 0" }}>
                          {[
                            {
                              key: "seg-asc",
                              label: "Segment A → Z",
                              onSelect: () => {
                                setBreakdownSortMode("segment-asc");
                                setBreakdownSortMenuOpen(false);
                                setValueSubmenuHover(null);
                              },
                              active: breakdownSortMode === "segment-asc",
                            },
                            {
                              key: "seg-desc",
                              label: "Segment Z → A",
                              onSelect: () => {
                                setBreakdownSortMode("segment-desc");
                                setBreakdownSortMenuOpen(false);
                                setValueSubmenuHover(null);
                              },
                              active: breakdownSortMode === "segment-desc",
                            },
                          ].map((opt) => (
                            <div
                              key={opt.key}
                              role="menuitem"
                              onClick={opt.onSelect}
                              className="mx-2 px-3 py-2 text-[13px] cursor-pointer rounded"
                              style={{
                                backgroundColor: opt.active ? (theme.bgOverlay || "var(--surface-sunken)") : "transparent",
                                color: opt.active ? (theme.textPrimary || "var(--text-primary)") : (theme.textSecondary || "var(--text-secondary)"),
                                fontWeight: opt.active ? 600 : 500,
                                borderLeft: opt.active ? `2px solid ${theme.primary}` : "2px solid transparent",
                              }}
                              onMouseEnter={() => setValueSubmenuHover(null)}
                            >
                              {opt.label}
                            </div>
                          ))}
                          <div
                            role="menuitem"
                            className="mx-2 px-3 py-2 text-[13px] cursor-pointer rounded flex items-center justify-between gap-2"
                            style={{
                              backgroundColor:
                                breakdownSortMode === "value-asc" ? (theme.bgOverlay || "var(--surface-sunken)") : "transparent",
                              color:
                                breakdownSortMode === "value-asc"
                                  ? (theme.textPrimary || "var(--text-primary)")
                                  : (theme.textSecondary || "var(--text-secondary)"),
                              fontWeight: breakdownSortMode === "value-asc" ? 600 : 500,
                              borderLeft:
                                breakdownSortMode === "value-asc"
                                  ? `2px solid ${theme.primary}`
                                  : "2px solid transparent",
                            }}
                            onMouseEnter={() => setValueSubmenuHover("asc")}
                          >
                            <span>Value 1 → 100</span>
                            <span style={{ opacity: 0.6 }} aria-hidden="true">
                              ›
                            </span>
                          </div>
                          <div
                            role="menuitem"
                            className="mx-2 px-3 py-2 text-[13px] cursor-pointer rounded flex items-center justify-between gap-2"
                            style={{
                              backgroundColor:
                                breakdownSortMode === "value-desc" ? (theme.bgOverlay || "var(--surface-sunken)") : "transparent",
                              color:
                                breakdownSortMode === "value-desc"
                                  ? (theme.textPrimary || "var(--text-primary)")
                                  : (theme.textSecondary || "var(--text-secondary)"),
                              fontWeight: breakdownSortMode === "value-desc" ? 600 : 500,
                              borderLeft:
                                breakdownSortMode === "value-desc"
                                  ? `2px solid ${theme.primary}`
                                  : "2px solid transparent",
                            }}
                            onMouseEnter={() => setValueSubmenuHover("desc")}
                          >
                            <span>Value 100 → 1</span>
                            <span style={{ opacity: 0.6 }} aria-hidden="true">
                              ›
                            </span>
                          </div>
                        </div>
                        {valueSubmenuHover && (
                          <div
                            style={{
                              borderLeft: `1px solid ${theme.border}`,
                              minWidth: 200,
                              padding: "8px 0",
                              maxHeight: 320,
                              overflowY: "auto",
                            }}
                            onMouseEnter={() => setValueSubmenuHover(valueSubmenuHover)}
                          >
                            {valueColumnOptions.map((col) => {
                              const dir = valueSubmenuHover;
                              const active =
                                (dir === "asc" && breakdownSortMode === "value-asc" && valueSortColumn === col.key) ||
                                (dir === "desc" && breakdownSortMode === "value-desc" && valueSortColumn === col.key);
                              return (
                                <div
                                  key={col.key === "total" ? "total" : `iv-${col.key}`}
                                  role="menuitem"
                                  onClick={() => applyValueSort(dir, col.key)}
                                  className="mx-2 px-3 py-2 text-[13px] cursor-pointer rounded"
                                  style={{
                                    backgroundColor: active ? (theme.bgOverlay || "var(--surface-sunken)") : "transparent",
                                    color: active ? (theme.textPrimary || "var(--text-primary)") : (theme.textSecondary || "var(--text-secondary)"),
                                    fontWeight: active ? 600 : 500,
                                    borderLeft: active ? `2px solid ${theme.primary}` : "2px solid transparent",
                                  }}
                                >
                                  {col.label}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>,
                      document.body
                    )}
                  </div>
                  {typeof onBreakdownChartTopNChange === "function" && breakdownChartTopN != null && (
                    <BreakdownTopValuesControl value={breakdownChartTopN} onChange={onBreakdownChartTopNChange} />
                  )}
                </div>
              ) : (
                firstColumnHeader
              )}
            </th>
            <th
              className={tableHeaderCellClass}
              style={{ borderRight: `1px solid ${theme.border}` }}
            >
              Total Profile(s)
            </th>
            {intervals.map((interval, index) => (
              <th
                key={index}
                className={tableHeaderCellClass}
                style={{
                  borderRight: index < intervals.length - 1 ? `1px solid ${theme.border}` : "none",
                  minWidth: 56,
                }}
              >
                {interval}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hasBreakdowns
            ? sortedBreakdownRows.map((segment, rowIndex) => (
                <React.Fragment key={segment.id}>
                  <tr className={tableStaticRowClass}>
                    <td
                      className={`${tableDataCellClass} whitespace-nowrap`}
                      style={{
                        color: theme.text,
                        borderRight: `1px solid ${theme.border}`,
                        fontWeight: 500,
                        minWidth: 96,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSegment(segment.id)}
                        className="inline-flex items-center gap-2"
                        style={{ color: theme.text, background: "transparent", border: "none", padding: 0 }}
                      >
                        <span style={{ width: 12, display: "inline-block", textAlign: "center" }}>
                          {expandedSegments[segment.id] ? "▾" : "▸"}
                        </span>
                        <span>{segment.label}</span>
                      </button>
                    </td>
                    <td
                      className={`${tableDataCellClass} text-right tabular-nums`}
                      style={{
                        color: theme.text,
                        borderRight: `1px solid ${theme.border}`,
                        fontWeight: 600,
                      }}
                    >
                      {segment.cohortSize}
                    </td>
                    {intervals.map((_, colIndex) => {
                      const value = segment.values[colIndex] ?? 0;
                      const isIncomplete = segment.isIncomplete[colIndex] === true;
                      const formattedValue = metric === "retention_rate"
                        ? `${formatNumber(value, { decimals: 2 })}%`
                        : formatNumber(value);
                      return (
                        <td
                          key={`${segment.id}-${colIndex}`}
                          className={`${tableDataCellClass} text-right tabular-nums`}
                          style={{
                            color: theme.text,
                            borderRight: colIndex < intervals.length - 1 ? `1px solid ${theme.border}` : "none",
                          }}
                        >
                          {formattedValue}{isIncomplete ? "*" : ""}
                        </td>
                      );
                    })}
                  </tr>
                  {expandedSegments[segment.id] && segment.children.map((cohortRow, childIndex) => (
                    <tr
                      key={`${segment.id}-child-${childIndex}`}
                      className={tableStaticRowClass}
                    >
                      <td
                        className={`${tableDataCellClass} whitespace-nowrap`}
                        style={{
                          color: theme.textMuted,
                          borderRight: `1px solid ${theme.border}`,
                          minWidth: 96,
                          paddingLeft: 28,
                        }}
                      >
                        {cohortRow.label}
                      </td>
                      <td
                        className={`${tableDataCellClass} text-right tabular-nums`}
                        style={{
                          color: theme.textMuted,
                          borderRight: `1px solid ${theme.border}`,
                        }}
                      >
                        {formatNumber(cohortRow.cohortSize || 0)}
                      </td>
                      {intervals.map((_, colIndex) => {
                        const value = cohortRow.values[colIndex] ?? 0;
                        const isIncomplete = cohortRow.isIncomplete[colIndex] === true;
                        const formattedValue = metric === "retention_rate"
                          ? `${formatNumber(value, { decimals: 2 })}%`
                          : formatNumber(value);
                        return (
                          <td
                            key={`${segment.id}-child-${childIndex}-${colIndex}`}
                            className={`${tableDataCellClass} text-right tabular-nums`}
                            style={{
                              color: theme.textMuted,
                              borderRight: colIndex < intervals.length - 1 ? `1px solid ${theme.border}` : "none",
                            }}
                          >
                            {formattedValue}{isIncomplete ? "*" : ""}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))
            : tableRows.map((cohort, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={tableStaticRowClass}
                >
                  <td
                    className={`${tableDataCellClass} whitespace-nowrap`}
                    style={{
                      color: theme.text,
                      borderRight: `1px solid ${theme.border}`,
                      fontWeight: 500,
                      minWidth: 96,
                    }}
                  >
                    {cohort.label || "Unknown"}
                  </td>
                  <td
                    className={`${tableDataCellClass} text-right tabular-nums`}
                    style={{
                      color: theme.text,
                      borderRight: `1px solid ${theme.border}`,
                      fontWeight: cohort.isSummary ? 600 : 400,
                    }}
                  >
                    {typeof cohort.cohortSize === "string"
                      ? cohort.cohortSize
                      : formatNumber(cohort.cohortSize || 0)}
                  </td>
                  {intervals.map((interval, colIndex) => {
                    const value = cohort.values && cohort.values[colIndex] !== undefined
                      ? cohort.values[colIndex]
                      : 0;
                    const isIncomplete = Array.isArray(cohort.isIncomplete) && cohort.isIncomplete[colIndex] === true;

                    const formattedValue = metric === "retention_rate"
                      ? `${formatNumber(value, { decimals: 2 })}%`
                      : formatNumber(value);

                    return (
                      <td
                        key={colIndex}
                        className={`${tableDataCellClass} text-right tabular-nums`}
                        style={{
                          color: theme.text,
                          borderRight: colIndex < intervals.length - 1 ? `1px solid ${theme.border}` : "none",
                        }}
                      >
                        {formattedValue}{isIncomplete ? "*" : ""}
                      </td>
                    );
                  })}
                </tr>
              ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}
